import React from 'react';

const fmtMoney = (value) => `$${(value ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtNum = (value) => (value ?? 0).toLocaleString('en-US');
const fmtResultValue = (value, metric) => metric === 'pv' ? fmtMoney(value) : fmtNum(value);

const ResultTable = ({ data, department, isDark }) => {
  const metric = data?.metric || (department === 'sales' ? 'ftd' : 'pv');
  const rows = data?.rows || [];
  const monstr = data?.monstrDay;
  const monstrText = monstr && (monstr.resultDay || 0) > 0 ? `${monstr.name} - ${fmtResultValue(monstr.resultDay, metric)}` : '';
  const total = data?.total || { resultMonth: 0 };
  const targetLabel = metric === 'ftd' ? 'Target FTD' : 'Target $';
  const fmtTarget = (value) => metric === 'ftd' ? fmtNum(value) : fmtMoney(value);
  const dayColor = (row) => {
    if (!row?.target) return department === 'sales' ? '#a78bfa' : '#34d399';
    return (row.resultDay || 0) >= (row.dailyTarget || 0) ? '#22c55e' : '#ef4444';
  };
  const monthColor = (row) => {
    if (!row?.target) return department === 'sales' ? '#a78bfa' : '#34d399';
    return (row.resultMonth || 0) >= (row.monthTargetToDate || row.target) ? '#22c55e' : '#ef4444';
  };

  return (
    <div
      className="rounded-lg border p-4"
      style={{
        background: isDark ? '#0a0e1a' : '#fff',
        borderColor: isDark ? '#1f2937' : '#e5e7eb',
      }}
    >
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] text-xs">
          <thead>
            <tr>
              {['Name', 'Result day', 'Result month', targetLabel, '% to target'].map(label => (
                <th
                  key={label}
                  className="border-b px-4 py-3 text-left font-semibold uppercase"
                  style={{
                    color: isDark ? '#9ca3af' : '#6b7280',
                    borderColor: isDark ? '#1f2937' : '#e5e7eb',
                  }}
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-10 text-center text-sm"
                  style={{ color: isDark ? '#6b7280' : '#9ca3af' }}
                >
                  No result data
                </td>
              </tr>
            ) : rows.map(row => (
              <tr
                key={row.name}
                className="transition-colors"
                style={{ borderTop: `1px solid ${isDark ? '#111827' : '#f3f4f6'}` }}
              >
                <td className="px-4 py-3 font-semibold" style={{ color: isDark ? '#f8fafc' : '#111827' }}>{row.name}</td>
                <td className="px-4 py-3 font-bold tabular-nums" style={{ color: dayColor(row) }}>{fmtResultValue(row.resultDay, metric)}</td>
                <td className="px-4 py-3 font-bold tabular-nums" style={{ color: monthColor(row) }}>{fmtResultValue(row.resultMonth, metric)}</td>
                <td className="px-4 py-3 tabular-nums" style={{ color: isDark ? '#cbd5e1' : '#374151' }}>{fmtTarget(row.target)}</td>
                <td className="px-4 py-3 tabular-nums" style={{ color: isDark ? '#cbd5e1' : '#374151' }}>{row.percentToTarget || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4 space-y-2 px-1 text-xs" style={{ color: isDark ? '#6b7280' : '#64748b' }}>
        <div><span className="font-semibold">Daily Monster:</span>{monstrText ? ` ${monstrText}` : ''}</div>
        <div><span className="font-semibold">Total:</span> {fmtResultValue(total.resultMonth, metric)}</div>
      </div>
    </div>
  );
};

const ResultPanel = ({
  data,
  department,
  isDark,
  loading,
  error,
  canSwitchDepartment,
  onDepartmentChange,
  title = 'Result',
  subtitle,
}) => {
  const description = subtitle || (department === 'sales' ? 'Sales shows FTD count' : 'Retention shows PV');

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{title}</div>
          <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{description}</div>
        </div>
        {canSwitchDepartment && (
          <div className={`inline-flex h-9 w-fit rounded-md border p-1 ${isDark ? 'border-gray-800 bg-[#0a0e1a]' : 'border-gray-200 bg-gray-50'}`}>
            {[
              { key: 'sales', label: 'Sales' },
              { key: 'retention', label: 'Retention' },
            ].map(item => (
              <button
                key={item.key}
                type="button"
                onClick={() => onDepartmentChange(item.key)}
                className={`rounded px-4 text-xs font-semibold transition-colors ${
                  department === item.key
                    ? 'bg-purple-600 text-white shadow-sm'
                    : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {error ? (
        <div className={`rounded-lg border p-8 text-center text-sm ${isDark ? 'border-red-900/40 bg-red-950/10 text-red-300' : 'border-red-200 bg-red-50 text-red-600'}`}>{error}</div>
      ) : loading ? (
        <div className={`rounded-lg border p-8 text-center text-sm ${isDark ? 'border-gray-800 bg-[#0f1729] text-gray-500' : 'border-gray-200 bg-white text-gray-400'}`}>Loading results...</div>
      ) : (
        <ResultTable data={data} department={department} isDark={isDark} />
      )}
    </div>
  );
};

export default ResultPanel;
